import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { useGuardianAcademyStore } from '@/store/guardianAcademy';
import { academyService } from '@/services/academy';
import { AcademyBrandingProvider } from '@/contexts/AcademyBrandingContext';
import { useAcademyHost } from '@/contexts/AcademyHostContext';
import { useAcademySlug } from '@/hooks/useAcademySlug';
import { DashboardLayout } from './DashboardLayout';
import { hasAnyRole, TierFeatureKey, normalizeSubscriptionTier } from '@court-sync/shared';
import { useAcademyFeatureAccess } from '@/hooks/useFeatureAccess';
import { useAcademyPaths } from '@/hooks/useAcademyPaths';
import { StarterPortalUnavailable } from '@/components/guardian/StarterPortalUnavailable';

interface GuardianAcademyLayoutProps {
  /** When set (e.g. academy lessons route gate), render instead of nested <Outlet />. */
  children?: ReactNode;
}

export function GuardianAcademyLayout({ children }: GuardianAcademyLayoutProps) {
  const academySlug = useAcademySlug();
  const { host, academy: hostAcademy } = useAcademyHost();
  const { user } = useAuthStore();
  const {
    academySlug: storeSlug,
    academy,
    setAcademyContext,
    clearAcademyContext,
  } = useGuardianAcademyStore();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!academySlug) {
      clearAcademyContext();
      return undefined;
    }
    if (host.mode === 'subdomain' && hostAcademy) {
      void Promise.resolve().then(() => {
        setAcademyContext(academySlug, hostAcademy.id, hostAcademy);
        setError(null);
      });
      return () => clearAcademyContext();
    }
    let mounted = true;
    academyService
      .resolveAcademyBySlug(academySlug)
      .then((acad) => {
        if (mounted) {
          setAcademyContext(academySlug, acad.id, acad);
          setError(null);
        }
      })
      .catch((err) => {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Academy not found');
        }
      });
    return () => {
      mounted = false;
      clearAcademyContext();
    };
  }, [academySlug, host.mode, hostAcademy, setAcademyContext, clearAcademyContext]);

  const loading = !!academySlug && !error && (storeSlug !== academySlug || !academy);
  const { hasAccess: hasPortalAccess } = useAcademyFeatureAccess(academy, TierFeatureKey.STUDENT_GUARDIAN_PORTAL);
  const { p } = useAcademyPaths(academy ?? { slug: academySlug ?? '' });

  if (!user || !hasAnyRole(user, ['guardian', 'student'])) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!academySlug) {
    return <Navigate to="/org-picker" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <p className="text-gray-600 dark:text-gray-400">Loading academy...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <a href="/org-picker" className="text-primary-600 hover:underline">
            Back to organization picker
          </a>
        </div>
      </div>
    );
  }

  if (academy && !hasPortalAccess && normalizeSubscriptionTier(academy.subscriptionTier) === 'starter') {
    return (
      <AcademyBrandingProvider primaryColor={academy.primaryColor} secondaryColor={academy.secondaryColor}>
        <DashboardLayout dashboardRole="guardian" academySlug={academySlug}>
          <StarterPortalUnavailable
            academy={academy}
            portalLabel="Student & guardian portal"
            publicLessonsPath={p('/lessons')}
          />
        </DashboardLayout>
      </AcademyBrandingProvider>
    );
  }

  return (
    <AcademyBrandingProvider primaryColor={academy.primaryColor} secondaryColor={academy.secondaryColor}>
      <DashboardLayout dashboardRole="guardian" academySlug={academySlug}>
        {children ?? <Outlet />}
      </DashboardLayout>
    </AcademyBrandingProvider>
  );
}
